import React, { useCallback, useEffect, useState } from 'react';

import { useUnmountEffect } from '@react-hookz/web';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { RowSelectionState } from '@tanstack/table-core';
import * as navigation from 'app-level/navigation';
import validationImgPath from 'assets/pngs/validation.png';
import { keys } from 'lodash';
import { DateTime } from 'luxon';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { Tooltip } from 'react-tooltip';
import { marketplaceAssetClassLabel } from 'types/assetClass';
import { useDialog } from 'ui-kit';
import ConfirmDialog from 'ui-kit/common/ConfirmDialog';

import { useMutation, useQuery } from '@apollo/client';

import { ListingState, PoolMarketStatus } from '__generated__/globalTypes';
import { ArchivePools, ArchivePoolsVariables } from 'mutation/__generated__/archivePool';
import { ARCHIVE_POOL } from 'mutation/archivePool';

import { GetPools } from 'query/__generated__/getPools';
import { GET_POOLS, Pool } from 'query/getPools';

import { PortfolioLayout } from './Portfolio/PortfolioLayout';
import { useSyncSelectedPoolsData } from './hooks/useSyncSelectedPoolsData';
import { usePortfolioContext } from './PortfolioContext';
import { restrictedResizeColumnIds } from '../../../app-level/config/TableRestrictions';
import { Button, DataDisplayTable, DataDisplayTableProps } from '../../../ui-kit';

interface PoolTableProps {
  data: Pool[];
  selection?: DataDisplayTableProps<Pool, keyof Pool>['selection'];
}

const PoolTable = ({ data, selection }: PoolTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const { formatNumber } = useIntl();

  const formatCentsToDollars = (cents: number): string => {
    if (isNaN(cents) || cents == null) return '-';
    return formatNumber(cents / 100, { style: 'currency', currency: 'USD' });
  };

  const toPercentage = (bps: number): string => {
    if (isNaN(bps) || bps == null) return '-';
    return formatNumber(bps / 10000, { style: 'percent', minimumFractionDigits: 2 });
  };

  const formatDateTime = (date: string): string => {
    if (!date) return '-';
    const luxonDate = DateTime.fromISO(date);
    return luxonDate.toFormat("MM/dd/yy ' @ ' h:mma ZZZZ");
  };

  const getCellValue = (pool: Pool, activeValue: string | React.ReactNode, inactiveValue = '') => {
    return pool.state !== ListingState.ACTIVE ? inactiveValue : activeValue;
  };

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);
  };

  const POOL_STATUS_BADGE: Record<PoolMarketStatus, { label: string; className: string }> = {
    [PoolMarketStatus.IN_MARKET]: {
      label: 'In Market',
      className: 'bg-green-400 text-green-900',
    },
    [PoolMarketStatus.TRADING]: {
      label: 'Trading',
      className: 'bg-yellow-300 text-yellow-900',
    },
    [PoolMarketStatus.TRADED]: {
      label: 'Traded',
      className: 'bg-slate-200 text-slate-600',
    },
    [PoolMarketStatus.OFF_MARKET]: {
      label: 'Off Market',
      className: 'bg-gray-900 text-white',
    },
  };

  const columns: ColumnDef<Pool, keyof Pool>[] = [
    {
      accessorKey: 'market_status',
      header: 'Pool Status',
      cell: (props) => {
        const { label, className } = POOL_STATUS_BADGE[props.row.original.market_status];
        return (
          <div
            className={`line-height[16px] inline-block w-[152px] rounded ${className} px-2 py-1 text-center font-['PT_Sans_Narrow'] text-[14px] font-bold`}
          >
            {label}
          </div>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Loan Pool Name',
    },
    {
      accessorKey: 'created_date',
      header: 'Date Created',
      cell: (props) => <span>{formatDateTime(props.getValue())}</span>,
    },
    {
      accessorKey: 'asset_class',
      header: 'Asset Class',
      cell: (props) => <span>{marketplaceAssetClassLabel(props.row.original.asset_class)}</span>,
    },
    {
      accessorKey: 't_current_balance_cents',
      header: 'UPB',
      cell: (props) => {
        const pool = props.row.original;
        return (
          <span>
            {getCellValue(pool, formatCentsToDollars(Number(props.getValue())), 'Calculating...')}
          </span>
        );
      },
      meta: {
        numeric: true,
        alignment: 'left',
      },
    },
    {
      accessorKey: 'loan_count',
      header: 'Total in Pool',
      cell: (props) => {
        const pool = props.row.original;
        return <span>{getCellValue(pool, props.getValue())}</span>;
      },
      meta: {
        numeric: true,
        alignment: 'left',
      },
    },
    {
      accessorKey: 'wa_coupon_bps',
      cell: (props) => {
        const pool = props.row.original;
        return <span>{getCellValue(pool, toPercentage(Number(props.getValue())))}</span>;
      },
      header: 'GWAC',
      meta: {
        numeric: true,
        alignment: 'left',
      },
    },
  ];

  return (
    <DataDisplayTable
      data={data}
      columns={columns}
      selection={selection}
      // Pool row identity is the pool's UUID — keeps the per-row
      // `data-testid="loan-row-select-radio-{id}"` stable across sort
      // changes and re-fetches, so Playwright can target a known pool
      // without depending on row index, accessible-name regex, or
      // viewport position.
      getRowId={(pool) => pool.id ?? ''}
      noDataMessage="Navigate to Loan Level Data to create a pool."
      // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
      sorting={{ state: sorting, onSortingChanged: handleSortingChange }}
      resizeCfg={restrictedResizeColumnIds.myPortfolio.poolManager}
    />
  );
};

const PoolManager = () => {
  const { setSelectedPoolsData } = usePortfolioContext();

  const { data, error: queryError } = useQuery<GetPools>(GET_POOLS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000,
  });
  const [archivePoolMutation] = useMutation<ArchivePools, ArchivePoolsVariables>(ARCHIVE_POOL);
  const { setPool } = navigation.usePoolUrl();
  const [selectedPools, setSelectedPools] = useState<RowSelectionState>({});

  const onSelectPool = useCallback(
    (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const newSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(selectedPools) : updaterOrValue;
      setSelectedPools(newSelection);
      const pools = data?.user.company.pools;
      // selectedKey is now the pool UUID (via DataDisplayTable's
      // `getRowId`), not the array index. Look up by id directly so
      // `usePoolUrl().setPool` keeps the `?pool=<id>` query param in
      // sync for deep-link / browser-history.
      const selectedKey = keys(newSelection)[0];
      if (pools && selectedKey !== undefined) {
        const pool = pools.find((p) => p.id === selectedKey);
        if (pool) {
          setPool(pool.id);
        }
      }
    },
    [data?.user.company.pools, selectedPools, setPool],
  );
  const archiveConfirmDialog = useDialog();

  const selectedPoolsArray = data?.user.company.pools?.filter((p) => selectedPools[p.id]) ?? [];

  useSyncSelectedPoolsData(selectedPoolsArray);

  useUnmountEffect(() => {
    setSelectedPoolsData([]);
  });

  // RowSelectionState keys are now the pool's UUID (via `getRowId` on
  // DataDisplayTable below), so resolve the selected pool by UUID
  // match, not array index.
  const selectedPoolKey = Object.keys(selectedPools)[0];
  const selectedPool = data?.user.company.pools.find((p) => p.id === selectedPoolKey);

  const canArchivePool = selectedPool && Object.keys(selectedPools).length > 0;

  const archivePool = async () => {
    if (!selectedPool) return;

    try {
      const result = await archivePoolMutation({
        variables: {
          input: {
            pool_ids: [selectedPool.id],
          },
        },
      });

      const firstResult = result.data?.archivePools?.results?.[0];
      const topLevelSuccess = result.data?.archivePools?.success === true;
      const perResultSuccess = firstResult ? firstResult.success === true : false;

      if (topLevelSuccess && perResultSuccess) {
        toast.success('Pool archived successfully');
        setSelectedPools({});
      } else {
        const message =
          result.data?.archivePools?.message ||
          firstResult?.message ||
          'Failed to archive pool. Please try again.';
        toast.error(message);
      }
    } catch (error) {
      console.error('Error in archiving pool:', error);

      let userErrorMessage = 'An unexpected error occurred while archiving the pool.';

      toast.error(userErrorMessage);
    }
  };

  const handleArchiveClick = () => {
    archiveConfirmDialog.openDialog();
  };

  return (
    <PortfolioLayout
      actions={
        <div className="flex">
          <Button
            data-tooltip-id={`archive-pool-tooltip`}
            disabled={!canArchivePool}
            size="large"
            type="primary"
            className="w-[160px]"
            label="Archive Pool"
            onClick={handleArchiveClick}
          >
            Archive
          </Button>
        </div>
      }
    >
      <>
        {!canArchivePool && (
          <Tooltip id={`archive-pool-tooltip`} place="left" variant="info">
            <span>You must select a pool to archive it.</span>
          </Tooltip>
        )}
        {queryError ? (
          <div>Error retrieving pools</div>
        ) : (
          <PoolTable
            data={data?.user.company.pools ?? []}
            selection={{
              selected: selectedPools,
              onSelectionChange: onSelectPool,
              enableMultiRowSelection: false,
            }}
          />
        )}
      </>

      <ConfirmDialog
        dialog={archiveConfirmDialog}
        message="Are you sure you want to archive this pool?"
        confirmButtonText="Archive"
        onConfirm={archivePool}
        image={validationImgPath}
        imageAlt="Validation"
        imageW="300px"
      />
    </PortfolioLayout>
  );
};

export default PoolManager;
